/**
 * Build A Box (CTR) — Alpine.js component
 * Stage 2: client-side quantity state, live totals, live loot ladder,
 * add-to-cart with the shared selling plan + Discount Kit property.
 *
 * Registered as `ctrBuildBox`. Bootstrapped per-section from
 * window.__ctrBuildBox[sectionId], a small JSON blob written by
 * sections/ctr-build-box-v2.liquid (see the <script> tag right before this
 * file loads). Only price/threshold data needed for math lives in that
 * blob — titles, images, pack labels, tier icons/labels stay server-
 * rendered in the Liquid markup; Alpine only layers reactivity on top.
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('ctrBuildBox', (config) => ({
    minItemCount: config.minItemCount || 1,
    progressBarMax: config.progressBarMax || 0,
    toggleProgressBarMax: config.toggleProgressBarMax || 0,
    sellingPlanId: config.sellingPlanId || null,
    subscriptionDiscountPercent: config.subscriptionDiscountPercent || 0,
    // Discount Kit line item property. The KEY is hardcoded below (see
    // addToCart) — only the VALUE is a section setting. Every product
    // added to the box gets the same property; the tiered offer logic
    // (2 items = free shipping, 4 = a gift, etc.) lives entirely inside
    // the Discount Kit offer configured against this value, not here.
    offerValue: config.offerValue || null,
    currencyCode: config.currencyCode || 'USD',
    text: config.text || {},
    products: config.products || [], // [{ id, variantId, price, available }]
    tiers: config.tiers || [], // [{ threshold, label, discountPercent, giftVariantIds, giftQuantity, giftAvailable, discountCode }]

    quantities: {},
    plan: 'subscribe',
    isAddingToCart: false,

    init() {
      this.products.forEach((p) => {
        this.quantities[p.id] = 0;
      });
    },

    // --- Quantity helpers -------------------------------------------------

    getQty(productId) {
      return this.quantities[productId] || 0;
    },

    setQty(productId, value) {
      let n = parseInt(value, 10);
      if (isNaN(n) || n < 0) n = 0;
      if (n > 99) n = 99;
      this.quantities[productId] = n;
    },

    increment(productId) {
      this.setQty(productId, this.getQty(productId) + 1);
    },

    decrement(productId) {
      this.setQty(productId, this.getQty(productId) - 1);
    },

    removeProduct(productId) {
      this.setQty(productId, 0);
    },

    findProduct(productId) {
      return this.products.find((p) => String(p.id) === String(productId));
    },

    lineTotalCents(productId) {
      const product = this.findProduct(productId);
      if (!product) return 0;
      return product.price * this.getQty(productId);
    },

    // --- Totals -------------------------------------------------------------

    get totalQty() {
      return Object.values(this.quantities).reduce((sum, qty) => sum + qty, 0);
    },

    get subtotalCents() {
      return this.products.reduce((sum, p) => sum + p.price * this.getQty(p.id), 0);
    },

    // Bundle (loot ladder tier) discount — display-only estimate of what the
    // highest reached tier's % is worth. Discount Kit still computes and
    // applies the real discount at checkout off the cart line item property;
    // this is just so the on-page total isn't misleading once a merchant has
    // filled in a tier's discount_percent setting.
    get bundleDiscountCents() {
      return Math.round((this.subtotalCents * this.activeTierDiscountPercent) / 100);
    },

    get subscribeDiscountCents() {
      const base = this.subtotalCents - this.bundleDiscountCents;
      return Math.round((base * this.subscriptionDiscountPercent) / 100);
    },

    get subscribeTotalCents() {
      return this.subtotalCents - this.bundleDiscountCents - this.subscribeDiscountCents;
    },

    get otpTotalCents() {
      return this.subtotalCents - this.bundleDiscountCents;
    },

    withSubscriptionDiscount(cents) {
      return Math.round((cents * (100 - this.subscriptionDiscountPercent)) / 100);
    },

    get displayTotalCents() {
      return this.plan === 'subscribe' ? this.subscribeTotalCents : this.otpTotalCents;
    },

    get isBelowMinimum() {
      return this.totalQty < this.minItemCount;
    },

    get remainingToMinimum() {
      return Math.max(this.minItemCount - this.totalQty, 0);
    },

    get minWarningText() {
      return (this.text.minCountWarning || 'Add [count] more').replace(
        '[count]',
        this.remainingToMinimum
      );
    },

    // --- Loot ladder ----------------------------------------------------

    get ladderPct() {
      if (this.progressBarMax <= 0) return 0;
      return Math.min((this.totalQty / this.progressBarMax) * 100, 100);
    },

    // Mobile-only collapsed toggle glance bar — separate scale from the
    // full ladder (see toggleProgressBarMax), so it can read as "full" at
    // a different item count than the tier thresholds use.
    get toggleLadderPct() {
      if (this.toggleProgressBarMax <= 0) return 0;
      return Math.min((this.totalQty / this.toggleProgressBarMax) * 100, 100);
    },

    tierPosition(threshold) {
      // Evenly spaced by index among the tiers, NOT proportional to the
      // threshold value — matches the Liquid-side default render (see
      // tier_count in sections/ctr-build-box-v2.liquid). Position = center
      // of each tier's equal-width slice, (index + 0.5) / count * 100 —
      // e.g. 16.7% / 50% / 83.3% for 3 tiers — same math as Figma's actual
      // flex: 1 + justify-center layout, NOT flush edge-to-edge
      // (0%/50%/100%), which would clip the first/last badge off-screen.
      if (this.tiers.length === 0) return 0;
      const index = this.tiers.findIndex((t) => t.threshold === threshold);
      if (index === -1) return 0;
      return ((index + 0.5) / this.tiers.length) * 100;
    },

    isTierReached(threshold) {
      return this.totalQty >= threshold;
    },

    tierBadgeText(threshold, template) {
      return (template || 'Add [n]').replace('[n]', threshold);
    },

    get nextTier() {
      let next = null;
      this.tiers.forEach((tier) => {
        if (this.totalQty < tier.threshold) {
          if (!next || tier.threshold < next.threshold) next = tier;
        }
      });
      return next;
    },

    get activeTier() {
      let active = null;
      this.tiers.forEach((tier) => {
        if (this.totalQty >= tier.threshold) {
          if (!active || tier.threshold > active.threshold) active = tier;
        }
      });
      return active;
    },

    get activeTierDiscountPercent() {
      return this.activeTier ? this.activeTier.discountPercent || 0 : 0;
    },

    get bundleDiscountLabel() {
      const active = this.activeTier;
      const threshold = active ? active.threshold : 0;
      return (this.text.bundleDiscountLabel || 'Bundle discount (build [count]+)').replace(
        '[count]',
        threshold
      );
    },

    get ladderStatusText() {
      const next = this.nextTier;
      if (!next) return this.text.lootLadderComplete || '';
      const remaining = next.threshold - this.totalQty;
      return (this.text.lootLadderProgress || '')
        .replace('[count]', remaining)
        .replace('[tier]', next.label);
    },

    // --- Formatting -------------------------------------------------------

    money(cents) {
      const amount = (cents || 0) / 100;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currencyCode,
        }).format(amount);
      } catch (e) {
        return '$' + amount.toFixed(2);
      }
    },

    // --- Add to cart --------------------------------------------------------

    get selectedProductIds() {
      return this.products.filter((p) => this.getQty(p.id) > 0).map((p) => p.id);
    },

    // --- Free gifts -------------------------------------------------------

    // Set after an add-to-cart when the gift sync couldn't add a gift
    // (e.g. sold out between page load and add).
    // Holds the tier label until the next add, since the box resets to 0
    // right after adding.
    giftSyncFailedLabel: '',

    // Reached tier whose gift is sold out per the server-rendered data.
    get unavailableGiftTier() {
      const tier = this.activeTier;
      if (!tier || !Array.isArray(tier.giftVariantIds) || !tier.giftVariantIds.length) return null;
      return tier.giftAvailable === false ? tier : null;
    },

    get giftNoticeText() {
      const tier = this.unavailableGiftTier;
      const label = tier ? tier.label : this.giftSyncFailedLabel;
      if (!label) return '';
      return (this.text.giftUnavailable || '').replace('[tier]', label);
    },

    // Tier → gift rules for assets/ctr-bab-gift-sync.js, stored in a hidden
    // cart attribute so the sync can run on any page (cart drawer, cart
    // page) without this section's settings. `ship` is the lowest tier
    // with a discount code (free shipping), used by the cart drawer's
    // free-shipping bar.
    get giftSyncConfig() {
      const codeTiers = this.tiers.filter((t) => t.discountCode).map((t) => t.threshold);
      return {
        v: 1,
        ship: codeTiers.length ? Math.min(...codeTiers) : 0,
        tiers: this.tiers
          .filter((t) => Array.isArray(t.giftVariantIds) && t.giftVariantIds.length)
          .map((t) => ({ n: t.threshold, q: t.giftQuantity || 1, g: t.giftVariantIds })),
      };
    },

    // --- Add to cart --------------------------------------------------------

    async cartRequest(path, body) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(root + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Cart request failed');
      }
      return data;
    },

    async addToCart() {
      if (this.isBelowMinimum || this.isAddingToCart) return;

      this.isAddingToCart = true;
      this.giftSyncFailedLabel = '';
      const giftTierLabel = this.activeTier ? this.activeTier.label : '';

      // Box items only. Gifts are NOT added here any more — the gift sync
      // (assets/ctr-bab-gift-sync.js) adds/removes them based on what's
      // actually in the cart, so re-adding a box, changing quantities in
      // the cart drawer, or reloading can't leave stale or duplicate gifts.
      const items = this.products
        .filter((p) => this.getQty(p.id) > 0)
        .map((p) => {
          // `_ctr_bab` marks a box line for the gift sync (only these count
          // toward tiers). `_offer` is the Discount Kit key (hardcoded key,
          // value from the section setting), unchanged from before.
          const properties = { _ctr_bab: '1' };
          if (this.offerValue) {
            properties._offer = this.offerValue;
          }
          const item = {
            id: p.variantId,
            quantity: this.getQty(p.id),
            properties,
          };
          if (this.plan === 'subscribe' && this.sellingPlanId) {
            item.selling_plan = this.sellingPlanId;
          }
          return item;
        });

      if (!items.length) {
        this.isAddingToCart = false;
        return;
      }

      try {
        await this.cartRequest('cart/update.js', {
          attributes: { _ctr_bab: JSON.stringify(this.giftSyncConfig) },
        });

        await this.cartRequest('cart/add.js', { items });

        // Reconcile gifts against the whole cart (box items already there
        // count too). Silent: we fire one cart:update ourselves below.
        if (window.CtrBabGiftSync) {
          const result = await window.CtrBabGiftSync.sync({ silent: true });
          if (result && result.unavailable && result.unavailable.length) {
            this.giftSyncFailedLabel = giftTierLabel || 'gift';
          }
        } else {
          console.warn('[Build A Box] Gift sync script missing — gifts not added.');
        }

        // Auto-apply discount codes (e.g. a free-shipping code unlocked at
        // 2 items) without making the customer type them in — same trick
        // as visiting yourstore.com/discount/CODE. Cumulative: every
        // currently reached tier with a code fires, not just the highest.
        // Awaited so the cart drawer renders with the code applied.
        await Promise.all(
          this.tiers
            .filter((t) => this.isTierReached(t.threshold) && t.discountCode)
            .map((t) => fetch(`/discount/${encodeURIComponent(t.discountCode)}`).catch(() => {}))
        );

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: 'ctr-build-box-v2',
              data: {
                source: 'ctr-build-box-v2',
                itemCount: items.length,
              },
            },
          })
        );

        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }

        this.products.forEach((p) => {
          this.quantities[p.id] = 0;
        });
      } catch (e) {
        console.error('[Build A Box] Add to cart error:', e);
        alert('Failed to add box to cart. Please try again.');
      } finally {
        this.isAddingToCart = false;
      }
    },
  }));
});